import { lt } from '@mews/framework';
import { AuthorizationType, PreauthorizationActions, PreauthorizationTableRecord } from '@mews-commander/data-access';
import { IconName } from '@optimus-web/icons';
import { WithAccountId } from '@mews-commander/types';
import { localizeCurrencyValue } from '@mews-commander/utils';
import { useDialogForm } from '@mews-commander/form';
import { OptimusProvider } from '@mews-commander/ui';
import { useCancelPreauthorization, useResendAuthorizationEmail } from '../../../queries';
import { ChargePreauthorizationDialogForm } from '../../forms/ProcessPayment/ChargePreauthorizationForm/ChargePreauthorizationDialogForm';
import { UpdatePreauthorizationDialogForm } from '../../forms/ProcessPayment/UpdatePreauthorizationForm/UpdatePreauthorizationDialogForm';
import { PaymentsTablesActionsMenu } from '../PaymentsTablesActionsMenu';

interface Props extends Pick<PreauthorizationTableRecord, 'amount' | 'card' | 'actions'> {
    preauthorizationId: string;
    refetchTableData: () => void;
}

export const PreauthorizationsActionsMenu = ({
    actions: preauthorizationActions,
    preauthorizationId,
    accountId,
    amount,
    card,
    refetchTableData,
}: Props & WithAccountId) => {
    const resendAuthorizationMail = useResendAuthorizationEmail();

    const [createChargePreauthorizationDialog, toggleChargePreauthorizationDialog] = useDialogForm({
        title: lt('ChargePreauthorizationDialogFormTitle'),
        renderForm: (props) => (
            <ChargePreauthorizationDialogForm
                accountId={accountId}
                preauthorizationId={preauthorizationId}
                {...props}
            />
        ),
        saveLabel: lt('Submit'),
        suspenseLoader: 'inline',
    });

    const [createUpdatePreauthorizationDialog, toggleUpdatePreauthorizationDialog] = useDialogForm({
        title: lt('UpdatePreauthorizationDialogFormTitle'),
        renderForm: (props) => (
            <UpdatePreauthorizationDialogForm
                accountId={accountId}
                preauthorizationId={preauthorizationId}
                {...props}
            />
        ),
        saveLabel: lt('Submit'),
        suspenseLoader: 'inline',
    });

    const cancelPreauthorization = useCancelPreauthorization({
        amount: localizeCurrencyValue(amount),
        lastFourDigists: card?.lastFourDigits || '',
    });

    const actions = [
        {
            key: PreauthorizationActions.ResendAuthorizationMail,
            icon: IconName.Mails,
            text: lt('ResendEmail'),
            onClick: () =>
                resendAuthorizationMail.mutate({
                    id: preauthorizationId,
                    authorizationType: AuthorizationType.Preauthorization,
                }),
        },
        {
            key: PreauthorizationActions.ChargePreauthorization,
            icon: IconName.Charge,
            text: lt('Charge'),
            onClick: toggleChargePreauthorizationDialog,
        },
        {
            key: PreauthorizationActions.UpdatePreauthorization,
            icon: IconName.Edit,
            text: lt('Update'),
            onClick: toggleUpdatePreauthorizationDialog,
        },
        {
            key: PreauthorizationActions.CancelPreauthorization,
            icon: IconName.Disable,
            text: lt('Cancel'),
            onClick: () =>
                cancelPreauthorization.mutate(
                    {
                        accountId,
                        preauthorizationId,
                    },
                    {
                        onSuccess: refetchTableData,
                    }
                ),
        },
    ];

    const actionsFiltered = actions.filter((action) => preauthorizationActions.includes(action.key));

    if (actionsFiltered.length === 0) {
        return null;
    }

    return (
        <>
            <PaymentsTablesActionsMenu actions={actionsFiltered} />
            <OptimusProvider density="spacious">
                {createChargePreauthorizationDialog()}
                {createUpdatePreauthorizationDialog()}
            </OptimusProvider>
        </>
    );
};
